/**
 * @voltxampere/engine — Transient predictor (linear extrapolation)
 *
 * SPECS §3.9 names "linear extrapolation" as the prototype predictor
 * for the LTE-based adaptive-step controller. Given the previous two
 * accepted solutions `x(t-h)` and `x(t-2h)`, the predictor estimates
 * `x(t)` by following the same straight line, scaled by the current
 * step's ratio to the previous step:
 *
 *   x_pred(t) = x(t−h) + (h / h_prev) · (x(t−h) − x(t−2h))
 *
 * On the very first transient step there is no `x(t-2h)`; the
 * predictor returns `x(t-h)` unchanged (the Newton corrector then
 * does the heavy lifting from a flat-line guess).
 *
 * The predictor's output also feeds the LTE estimate: the Newton-
 * corrected `x_corr` minus this `x_pred` gives the local truncation
 * error norm the adaptive controller uses to decide accept/reject.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ContractViolationError } from "@voltxampere/shared";
/**
 * Linearly extrapolate the next solution from two prior accepted
 * snapshots.
 *
 * @param xPrev      x(t − h_prev), the most recent accepted solution.
 * @param xPrevPrev  x(t − 2 h_prev) — null on the first transient
 *                   step, in which case `xPrev` is returned as-is.
 * @param h          the new (proposed) step size.
 * @param hPrev      the previous accepted step's size. Must be > 0
 *                   when `xPrevPrev` is non-null.
 */
export function linearPredictor(xPrev, xPrevPrev, h, hPrev) {
    if (!Number.isFinite(h) || h <= 0) {
        throw new ContractViolationError(`linearPredictor: h must be a positive finite number, got ${String(h)}`);
    }
    if (xPrevPrev === null) {
        // First step — no second history point. Cold-start the corrector
        // with the previous solution as the initial guess.
        return new Float64Array(xPrev);
    }
    if (!Number.isFinite(hPrev) || hPrev <= 0) {
        throw new ContractViolationError(`linearPredictor: hPrev must be a positive finite number when xPrevPrev is non-null, got ${String(hPrev)}`);
    }
    if (xPrev.length !== xPrevPrev.length) {
        throw new ContractViolationError(`linearPredictor: xPrev length ${String(xPrev.length)} ≠ xPrevPrev length ${String(xPrevPrev.length)}`);
    }
    const ratio = h / hPrev;
    const pred = new Float64Array(xPrev.length);
    for (let i = 0; i < xPrev.length; i++) {
        const a = xPrev[i] ?? 0;
        const b = xPrevPrev[i] ?? 0;
        pred[i] = a + ratio * (a - b);
    }
    return pred;
}
//# sourceMappingURL=predictor.js.map